'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { t } from '@lingui/macro';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Stack, Tooltip } from '@mui/material';
import type { StackProps } from '@mui/material/Stack';
import {
  Button,
  type ButtonProps,
  DatePickerInput,
  FavoriteRadioGroup,
  TextFieldInput,
} from '@prismo-io/design-system';
import {
  ExperienceCreatedFrom,
  ExperienceCreatedFromT,
} from '@prismo-io/schemas';
import {
  type FC,
  type ReactNode,
  useCallback,
  useMemo,
  useTransition,
} from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useDeleteExperience } from '../../../hooks/use-delete-experience';
import { useSendExperience } from '../../../hooks/use-send-experience';
import { DomainsAutocompleteInput } from '../../inputs/domains';
import { ExperienceNameAutocompleteInput } from '../../inputs/experience-name';
import { ExperienceTypesAutocompleteInput } from '../../inputs/experience-types';
import {
  ExperienceSchema,
  type ExperienceSchemaNullableT,
  type ExperienceSchemaT,
} from './schema';

type ExperienceFormProps = {
  defaultValues?: Partial<ExperienceSchemaT> | undefined;
  onComplete?: SubmitHandler<ExperienceSchemaT>;
  onDelete?: () => void;
  withFavorite?: boolean;
  needResetAfterSubmit?: boolean;
  submitProps?: {
    button?: ButtonProps;
    alignItem?: StackProps['alignItems'];
    justifyContent?: StackProps['justifyContent'];
  };
  submitBtns?: ReactNode;
  marginY?: number;
  needDeleteBtn?: boolean;
};

const emptyValuesForm = {
  id: null,
  name: '',
  contractType: null,
  company: '',
  yearStart: null,
  yearEnd: null,
  favorite: null,
  domains: [],
  createdFrom: ExperienceCreatedFrom.Enum.PRISMO,
} satisfies ExperienceSchemaNullableT;

export const ExperienceForm: FC<ExperienceFormProps> = (props) => {
  const {
    defaultValues = {},
    onComplete = undefined,
    onDelete = undefined,
    withFavorite = false,
    submitProps = {},
    submitBtns = undefined,
    marginY = 0,
    needDeleteBtn = false,
  } = props;

  const {
    button = { children: t`+ Ajouter` },
    alignItem = 'center',
    justifyContent = 'end',
  } = submitProps;

  const formMethods = useForm<ExperienceSchemaNullableT>({
    mode: 'onTouched',
    resolver: zodResolver(ExperienceSchema),
    defaultValues: {
      ...emptyValuesForm,
      ...defaultValues,
    },
  });

  const {
    control,
    reset,
    watch,
    setValue,
    formState: { isValid, isSubmitting, isSubmitSuccessful },
  } = formMethods;

  const watchName = watch('name');

  const withDomains = useMemo(() => typeof watchName === 'string', [watchName]);

  const [isTransiting, startTransition] = useTransition();

  const isLoading = useMemo(
    () => isTransiting || isSubmitting,
    [isTransiting, isSubmitting]
  );

  const { trigger: deleteExperience } = useDeleteExperience();

  const onDeleteItem = () => {
    // const validData = ExperienceSchema.parse(defaultValues);
    if (defaultValues.id) {
      deleteExperience(defaultValues.id, {
        onSuccess: async () => {
          startTransition(() => {
            if (onDelete) onDelete();
          });
        },
      });
    }
  };

  const { trigger: sendExperience } = useSendExperience();

  const onSubmit: SubmitHandler<ExperienceSchemaNullableT> = (data) => {
    const validData = ExperienceSchema.parse(data);
    const {
      id,
      name,
      company,
      contractType,
      yearStart,
      yearEnd,
      domains,
      favorite,
      createdFrom,
    } = validData;
    if (!name || !contractType) {
      throw new Error("Name can't be null");
    }

    return sendExperience(
      {
        id: id || null,
        customName: typeof name === 'string' ? name : null,
        designation: {
          name: typeof name === 'string' ? null : name.name,
          code: typeof name === 'string' ? null : name.code,
          referential_id: typeof name === 'string' ? null : name.referential_id,
        },
        type_id: contractType?.id || null,
        company: company || null,
        yearStart: yearStart?.toDateString() || null,
        yearEnd: yearEnd?.toDateString() || null,
        domains: domains || [],
        favorite: favorite || null,
        createdFrom,
      },
      {
        onSuccess: async (res) => {
          setValue('id', res.id);

          const eventName = id ? 'experience_updated' : 'experience_created';

          startTransition(() => {
            if (!id) {
              reset(emptyValuesForm);
            }
            if (onComplete) {
              onComplete({ id: res.id, ...validData });
            }
          });
        },
      }
    );
  };

  const startAt = watch('yearStart');
  const endAt = watch('yearEnd');

  const shouldDisableYearStart = useCallback(
    (date: Date) => {
      if (!endAt || !date) {
        return false;
      }

      const yearEnd = new Date(endAt).getFullYear();
      const year = date.getFullYear();

      return year > yearEnd;
    },
    [endAt]
  );

  const shouldDisableMonthStart = useCallback(
    (date: Date) => {
      if (!endAt || !date) {
        return false;
      }

      const yearEnd = new Date(endAt).getFullYear();
      const year = date.getFullYear();

      const monthEnd = new Date(endAt).getMonth();
      const month = date.getMonth();

      return yearEnd === year && month > monthEnd;
    },
    [endAt]
  );

  const shouldDisableYearEnd = useCallback(
    (date: Date) => {
      if (!startAt || !date) {
        return false;
      }

      const yearStart = new Date(startAt).getFullYear();
      const year = date.getFullYear();

      return year < yearStart;
    },
    [startAt]
  );

  const shouldDisableMonthEnd = useCallback(
    (date: Date) => {
      if (!startAt || !date) {
        return false;
      }

      const yearStart = new Date(startAt).getFullYear();
      const year = date.getFullYear();

      const monthStart = new Date(startAt).getMonth();
      const month = date.getMonth();

      return yearStart === year && month < monthStart;
    },
    [startAt]
  );

  return (
    <FormProvider {...formMethods}>
      <Stack
        spacing={2}
        marginY={marginY}
        component="form"
        onSubmit={formMethods.handleSubmit(onSubmit)}
      >
        {/* <pre>{JSON.stringify(watchName, null, 2)}</pre> */}

        <Tooltip
          title={t`Commencez à écrire les premières lettres de votre expérience, pour la trouver.`}
          placement="top-end"
        >
          <Stack>
            <ExperienceNameAutocompleteInput
              control={control}
              name="name"
              label={t`Nom de l'expérience`}
              disabled={isLoading}
              required
            />
          </Stack>
        </Tooltip>
        <ExperienceTypesAutocompleteInput
          control={control}
          name="contractType"
          label={t`Type de contrat`}
          disabled={isLoading}
          required
        />
        <TextFieldInput
          control={control}
          name="company"
          label={t`Au sein de l'entreprise`}
          disabled={isLoading}
        />
        <Stack direction="row" justifyContent="space-between">
          <DatePickerInput
            control={control}
            name="yearStart"
            label={t`Date de début`}
            views={['year', 'month']}
            disabled={isLoading}
            disableFuture
            shouldDisableYear={shouldDisableYearStart}
            shouldDisableMonth={shouldDisableMonthStart}
          />
          <DatePickerInput
            control={control}
            name="yearEnd"
            label={t`Date de fin`}
            views={['year', 'month']}
            disabled={isLoading}
            shouldDisableYear={shouldDisableYearEnd}
            shouldDisableMonth={shouldDisableMonthEnd}
          />
        </Stack>
        {withDomains && (
          <DomainsAutocompleteInput
            control={control}
            name="domains"
            label={t`Domaines`}
            disabled={isLoading}
            required
          />
        )}
        {withFavorite && (
          <FavoriteRadioGroup
            control={control}
            name="favorite"
            label={t`Favori`}
            disabled={isLoading}
          />
        )}
        {!!submitBtns ? (
          submitBtns
        ) : (
          <Stack
            direction="row"
            justifyContent={justifyContent}
            alignItems={alignItem}
          >
            {needDeleteBtn && (
              <Tooltip title={t`Supprimer l'expérience`} placement="right">
                <IconButton
                  aria-label="delete"
                  type="button"
                  onClick={onDeleteItem}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            <Button
              variant="contained"
              color="gradient"
              {...button}
              type="submit"
              loading={isLoading}
              disabled={!isValid || !watchName || isLoading}
            />
          </Stack>
        )}
      </Stack>
    </FormProvider>
  );
};
